import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * useElementResize: runs `onResize` whenever the element's box changes.
 *
 * Docked side panels change the width of a page's content with no window
 * resize event, both when a panel docks and continuously while its drag
 * handle is moved, so there is no transition to hook either. Canvas pages that
 * keep their own pixel-sized surface (cytoscape, leaflet) have to watch their
 * container instead.
 *
 * The callback is deferred to the next animation frame. ResizeObserver
 * already fires at most once per frame, and the extra hop keeps a callback
 * that resizes a child canvas out of the observation cycle that triggered it,
 * which is what raises "ResizeObserver loop completed with undelivered
 * notifications".
 *
 * The callback may change identity every render (an inline arrow is fine):
 * it is read through a ref, so the observer is attached once per element.
 */
export function useElementResize(ref: RefObject<HTMLElement | null>, onResize: () => void): void {
  const onResizeRef = useRef(onResize);
  useEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  const [el, setEl] = useState<HTMLElement | null>(null);

  // The element can mount LATER than the hook: a call site that renders a
  // loading branch first (a spinner, then the canvas) has no element on the render where
  // the observer effect would attach, and a ref object never notifies when it
  // fills in, so keying the effect on the ref alone silently observes nothing
  // forever. Re-read it after every commit instead; setState bails out when
  // the value is unchanged, so this costs a comparison on ordinary renders.
  // Setting the same element back is a no-op (useState bails out), so the
  // rule's "infinite chain of updates" cannot happen here, and [ref] is
  // exactly the dependency list that would reintroduce the bug.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setEl(ref.current);
  });

  useEffect(() => {
    if (!el) return;

    let raf = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => onResizeRef.current());
    });
    observer.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [el]);
}
